import {
  BellAlert,
  BellAlertDone,
  InformationCircleSolid,
} from "@medusajs/icons"
import { clx, Button, Drawer, Heading, IconButton, Text } from "@medusajs/ui"
import { TFunction } from "i18next"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
  MarketplaceNotification,
  MarketplaceNotificationListResponse,
  useMarketplaceNotificationUnreadCount,
  useMarkAllNotificationsAsRead,
  useMarkNotificationsAsRead,
  marketplaceNotificationQueryKeys,
} from "../../../hooks/api"
import { useDate } from "../../../hooks/use-date"
import { sdk } from "../../../lib/client"
import {
  getSubmittedCardDisplayContent,
  handleNotificationItemClick,
  isSubmittedProductApprovalNotification,
} from "./notification-utils"
import { InfiniteList } from "../../common/infinite-list"

export const ProductApprovalSubmittedCard = ({
  notification,
}: {
  notification: MarketplaceNotification
}) => {
  const { title, content, productName, productThumbnail } =
    getSubmittedCardDisplayContent(notification)

  return (
    <div className="flex w-full items-start gap-3">
      {productThumbnail ? (
        <img
          src={productThumbnail}
          alt={productName ?? "Product thumbnail"}
          className="h-12 w-12 rounded-md object-cover"
          loading="lazy"
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <Text size="small" leading="compact" weight="plus" className="truncate">
          {title}
        </Text>
        <Text
          className="text-ui-fg-subtle whitespace-pre-line break-words"
          size="small"
        >
          {content}
        </Text>
      </div>
    </div>
  )
}

const NotificationBody = ({
  notification,
}: {
  notification: MarketplaceNotification
}) => {
  if (isSubmittedProductApprovalNotification(notification)) {
    return <ProductApprovalSubmittedCard notification={notification} />
  }

  return (
    <>
      <Text size="small" leading="compact" weight="plus">
        {notification.title}
      </Text>
      {!!notification.content && (
        <Text className="text-ui-fg-subtle whitespace-pre-line" size="small">
          {notification.content}
        </Text>
      )}
    </>
  )
}

const NotificationItem = ({
  notification,
}: {
  notification: MarketplaceNotification
}) => {
  const unread = notification.read_at === null
  const { getRelativeDate } = useDate()
  const { mutateAsync: markAsRead } = useMarkNotificationsAsRead()
  const navigate = useNavigate()

  const handleClick = useCallback(async () => {
    await handleNotificationItemClick({
      notification,
      unread,
      markAsRead,
      navigate,
    })
  }, [unread, notification, markAsRead, navigate])

  return (
    <div
      className="relative flex cursor-pointer items-start gap-3 border-b p-6 transition-colors hover:bg-ui-bg-base-hover"
      onClick={handleClick}
    >
      <div className="text-ui-fg-muted flex w-5 shrink-0 items-start justify-center pt-1">
        <InformationCircleSolid />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <NotificationBody notification={notification} />
          </div>
          <div className="align-center flex shrink-0 items-center justify-center gap-2">
            <Text
              as={"span"}
              className={clx("text-ui-fg-subtle", {
                "text-ui-fg-base": unread,
              })}
              size="small"
              leading="compact"
              weight="plus"
            >
              {getRelativeDate(notification.created_at)}
            </Text>
            {unread && (
              <div className="bg-ui-bg-interactive h-2 w-2 rounded" role="status" />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const Notifications = () => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const { count: unreadCount } = useMarketplaceNotificationUnreadCount()
  const hasUnread = unreadCount > 0

  const { mutateAsync: markAllAsRead } = useMarkAllNotificationsAsRead()

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "n" && (e.metaKey || e.ctrlKey)) {
        setOpen((prev) => !prev)
      }
    }

    document.addEventListener("keydown", onKeyDown)

    return () => {
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [])

  const handleOnOpen = (shouldOpen: boolean) => {
    setOpen(shouldOpen)
  }

  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead()
  }, [markAllAsRead])

  return (
    <Drawer open={open} onOpenChange={handleOnOpen}>
      <Drawer.Trigger asChild>
        <span className="relative inline-flex overflow-visible">
          <IconButton
            variant="transparent"
            size="small"
            className="text-ui-fg-muted hover:text-ui-fg-subtle"
          >
            {hasUnread ? <BellAlertDone /> : <BellAlert />}
          </IconButton>
          {hasUnread && !open && (
            <span className="bg-ui-bg-interactive absolute -right-1 -top-1 z-10 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </span>
      </Drawer.Trigger>
      <Drawer.Content>
        <Drawer.Header>
          <div className="flex w-full items-center justify-between">
            <Drawer.Title asChild>
              <Heading>{t("notifications.domain")}</Heading>
            </Drawer.Title>
            {hasUnread && (
              <Button
                variant="transparent"
                size="small"
                onClick={handleMarkAllAsRead}
              >
                {t("notifications.markAllAsRead")}
              </Button>
            )}
          </div>
          <Drawer.Description className="sr-only">
            {t("notifications.accessibility.description")}
          </Drawer.Description>
        </Drawer.Header>
        <Drawer.Body className="overflow-y-auto px-0">
          <InfiniteList<
            MarketplaceNotificationListResponse,
            MarketplaceNotification,
            Record<string, any>
          >
            responseKey="notifications"
            queryKey={marketplaceNotificationQueryKeys.all}
            queryFn={(params) =>
              sdk.client.fetch<MarketplaceNotificationListResponse>(
                `/admin/marketplace-notifications?limit=${params?.limit ?? 20}&offset=${params?.offset ?? 0}`,
                { method: "GET" }
              )
            }
            queryOptions={{
              enabled: open,
            }}
            renderEmpty={() => <NotificationsEmptyState t={t} />}
            renderItem={(notification) => {
              return (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                />
              )
            }}
          />
        </Drawer.Body>
      </Drawer.Content>
    </Drawer>
  )
}

const NotificationsEmptyState = ({ t }: { t: TFunction }) => {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <BellAlertDone />
      <Text size="small" leading="compact" weight="plus" className="mt-3">
        {t("notifications.emptyState.title")}
      </Text>
      <Text
        size="small"
        className="text-ui-fg-muted mt-1 max-w-[294px] text-center"
      >
        {t("notifications.emptyState.description")}
      </Text>
    </div>
  )
}
